import { useRouter } from 'next/router';

import { useRef } from 'react';

import { usePrevUrlStore } from '@stores/usePrevUrlStore';
import useToastStore from '@stores/useToastStore';

import { Platform, ProductType } from '@constants';
import { useGlobalContext } from '@contexts/global';

import { ActionSheetOption } from '../components/common/ActionSheet';
import type { ShareSocial } from '../components/modal/ShareModal';
import {
  EmptyObject,
  ICharacterPost,
  ICommunityPost,
  IMyStoryDetail,
  IPaymentItem,
  IUserMarketInventory,
} from '../model';
import { AdType } from './ad';
import { ChatieIOSInterface, useAnalytics } from './analytics';
import { isEnv, jsonStringify, openDeepLink } from './misc';

type Value = string | number;

type Params = { [key: string]: Value };

type UnionToMap<U extends { key: string; value?: object }> = {
  [K in U['key']]: Extract<U, { key: K }>['value'];
};

export type ChatieAndroidInterface = {
  [key in Extract<keyof ChatieWebEvents, string>]: (...arg: unknown[]) => void;
};

function log(key: string, name: string, data: object) {
  if (!isEnv('production')) {
    console.log(`%c${key}`, 'color: #20a2ae;', name, data);
  }
}

export class IOSInterface {
  private interface?: ChatieIOSInterface;

  constructor() {
    if (typeof window !== 'undefined') {
      this.interface = window.webkit?.messageHandlers?.chatie;
    }
  }

  log = (command: string, name: string | undefined, data: object) => {
    if (this.interface) {
      const message = {
        command,
        name,
        ...data,
      };
      this.interface.postMessage(message);
    }
  };
}

/** Web events */
type WebCommunicatedData =
  | {
      key: 'firebaseIdToken';
      value: undefined;
    }
  | {
      key: 'screen';
      value: {
        screenName: string;
        parameters: string;
      };
    }
  | {
      key: 'phoneCertificationId';
      value: {
        transactionId: string;
      };
    };
type WebCommunicatedDataMap = UnionToMap<WebCommunicatedData>;

type ChatieWebEvents = {
  initialized: EmptyObject;
  startMaintenance: EmptyObject;
  openPage: { path: string };
  openUrl: { url: string };
  openExtUrl: { url: string; hasToken: boolean };
  openPopUpPage: { url: string };
  openPayment: { storyId: number; episodeId: number };
  closePage: EmptyObject;
  closePageOnBack: EmptyObject;
  reloadTab: { tab: number };
  mutate: {
    action: keyof WebInterfaceMutateActions;
    jsonData: string;
  };
  select: { name: string; options: string };
  data: { key: keyof WebCommunicatedDataMap; value?: string };
  setStatusBarColor: { color: 'white' | 'black' };
  leave: EmptyObject;
  share: {
    type: ShareSocial;
    url: string;
    title: string;
    summary: string;
    coverImage: string;
  };
  showStatusBar: EmptyObject;
  hideStatusBar: EmptyObject;
  loadAd: {
    adType: AdType;
    unitId: string;
    provider?: string;
  };
  viewAd: {
    adType: AdType;
    unitId: string;
    provider?: string;
  };
  showAppReview: EmptyObject;
  showToast: { message: string; length: number };
  openPhoneCertification: Record<string, never>;
  setIsBottomInputPage: { isBottomInput: boolean };
  openOssLicense: EmptyObject;
  setSwipeEnable: { isEnable: boolean };
  openSetting: EmptyObject;
  openAppSetting: EmptyObject;
  connectSNS: EmptyObject;
  getPushSetting: EmptyObject;
  changeInitialTab: { type: string };
  getInitialTab: EmptyObject;
  purchaseStoreItemByType: {
    product: string;
  };
  purchaseStoreItem: { bundleID: string };

  openMarket: EmptyObject;
  reSubscribeStoreItem: {
    product: string;
  };
  initPurchase: {
    paymentItems: string;
  };
  initializeBillingClient: EmptyObject;
  openOfferwall: { type: string };
  initOfferwall: { userID: string } | EmptyObject;
  changeDevUrl: { url: string };
  changeNextDevUrl: { url: string };
  shareImageWithText: {
    image: string;
    text: string;
  };
  setBackButtonState: {
    isAdShowing: boolean;
    shouldBlockBackButton: boolean;
  };
};

type WebInterfaceMutateActions = {
  create_story: IMyStoryDetail & { isSafety: boolean };
  update_story: IMyStoryDetail & { isSafety: boolean };
  delete_story: { id: number };
  update_story_active: { id: number; isActive: boolean };
  use_market_inventory: Pick<
    IUserMarketInventory,
    'id' | 'marketItem' | 'status'
  >;
  create_community: ICommunityPost;
  update_community: ICommunityPost;
  create_character_community: ICharacterPost;
  update_character_community: ICharacterPost;
};

class WebPayload<K extends keyof ChatieWebEvents, T = ChatieWebEvents[K]> {
  private data: Array<{ key: string; value: Value }> = [];

  push<U extends Extract<keyof T, string>>(key: U, value: T[U]) {
    this.data.push({ key: key, value: value as unknown as Value });
  }

  get values() {
    return this.data;
  }

  get android(): Array<Value> {
    return this.data.map((d) => d.value);
  }

  get ios(): Params {
    return this.data.reduce((acc, d) => {
      return {
        ...acc,
        [d.key]: d.value,
      };
    }, {});
  }
}

export class WebInterface {
  androidInterface?: ChatieAndroidInterface;
  iosInterface: IOSInterface;

  private static instance: WebInterface;

  public static getInstance() {
    return this.instance || (this.instance = new this());
  }

  constructor() {
    if (typeof window !== 'undefined') {
      this.androidInterface = window.ChatieAndroidInterface;
    }
    this.iosInterface = new IOSInterface();
  }

  get isNative(): boolean {
    return !!(
      this.androidInterface ||
      (typeof window !== 'undefined' && window.webkit?.messageHandlers?.chatie)
    );
  }

  _dispatch = <K extends keyof ChatieWebEvents>(
    command: K,
    payload: WebPayload<K>
  ) => {
    log('web', command, payload.ios);
    if (
      this.androidInterface &&
      typeof this.androidInterface?.[command] === 'function'
    ) {
      try {
        this.androidInterface?.[command](...payload.android);
      } catch (e) {
        console.warn('interface error:', e);
      }
    }
    this.iosInterface.log(command, undefined, payload.ios);
  };

  initialized = () => {
    this._dispatch('initialized', new WebPayload());
  };

  startMaintenance() {
    this._dispatch('startMaintenance', new WebPayload());
  }

  openLegacyPage = (path: string) => {
    const payload = new WebPayload<'openUrl'>();
    const url = new URL(path, process.env.NEXT_PUBLIC_DOMAIN_LEGACY);
    url.searchParams.set('from', 'next');
    payload.push('url', url.href);
    this._dispatch('openUrl', payload);
  };
  setBackButtonState = (
    isAdShowing: boolean,
    shouldBlockBackButton: boolean
  ) => {
    const payload = new WebPayload<'setBackButtonState'>();
    payload.push('isAdShowing', isAdShowing);
    payload.push('shouldBlockBackButton', shouldBlockBackButton);

    this._dispatch('setBackButtonState', payload);
  };

  openPage = (path: string) => {
    const payload = new WebPayload<'openUrl'>();
    payload.push('url', `${process.env.NEXT_PUBLIC_DOMAIN}${path}`);
    this._dispatch('openUrl', payload);
  };

  openUrl = (url: string) => {
    const payload = new WebPayload<'openUrl'>();
    payload.push('url', url);
    this._dispatch('openUrl', payload);
  };
  openMarket = () => {
    this._dispatch('openMarket', new WebPayload());
  };

  openExtUrl = (url: string, hasToken?: boolean) => {
    const payload = new WebPayload<'openExtUrl'>();
    payload.push('url', url);
    payload.push('hasToken', hasToken ?? false);
    this._dispatch('openExtUrl', payload);
  };

  openPopUpPage = (url: string) => {
    const payload = new WebPayload<'openPopUpPage'>();
    payload.push('url', url);
    this._dispatch('openPopUpPage', payload);
  };

  openPayment = (storyId: number, episodeId: number) => {
    const payload = new WebPayload<'openPayment'>();
    payload.push('storyId', storyId);
    payload.push('episodeId', episodeId);
    this._dispatch('openPayment', payload);
  };

  closePage = () => {
    this._dispatch('closePage', new WebPayload());
  };

  closePageOnBack = () => {
    this._dispatch('closePageOnBack', new WebPayload());
  };

  reloadTab = (index: number) => {
    const payload = new WebPayload<'reloadTab'>();
    payload.push('tab', index);
    this._dispatch('reloadTab', payload);
  };

  mutate = <T extends keyof WebInterfaceMutateActions>(
    action: T,
    mutateData: WebInterfaceMutateActions[T]
  ) => {
    const payload = new WebPayload<'mutate'>();
    payload.push('action', action);
    payload.push('jsonData', jsonStringify(mutateData));
    this._dispatch('mutate', payload);
  };

  openActionSheet = (name: string, options: ActionSheetOption[]) => {
    const payload = new WebPayload<'select'>();
    payload.push('name', name);
    payload.push('options', jsonStringify(options, false));
    this._dispatch('select', payload);
  };

  data = <K extends keyof WebCommunicatedDataMap>(
    key: K,
    value?: WebCommunicatedDataMap[K]
  ) => {
    const payload = new WebPayload<'data'>();
    payload.push('key', key);
    if (value) {
      payload.push('value', jsonStringify(value));
    }
    this._dispatch('data', payload);
  };

  setStatusBarColor = (color: 'white' | 'black') => {
    const payload = new WebPayload<'setStatusBarColor'>();
    payload.push('color', color);
    this._dispatch('setStatusBarColor', payload);
  };

  showStatusBar = () => {
    const payload = new WebPayload();
    this._dispatch('showStatusBar', payload);
  };

  hideStatusBar = () => {
    const payload = new WebPayload();
    this._dispatch('hideStatusBar', payload);
  };

  leave = () => {
    this._dispatch('leave', new WebPayload());
  };

  share = (
    type: ShareSocial,
    url: string,
    title: string,
    summary: string,
    coverImage: string
  ) => {
    if (!this.isNative) {
      if (navigator.share) {
        navigator.share({ title, text: summary, url }).catch(() => {});
      } else {
        navigator.clipboard?.writeText(url);
        this.showToast('링크가 복사되었습니다');
      }
      return;
    }
    const payload = new WebPayload<'share'>();
    payload.push('type', type);
    payload.push('url', url);
    payload.push('title', title);
    payload.push('summary', summary);
    payload.push('coverImage', coverImage);
    this._dispatch('share', payload);
  };
  shareImageWithText = (image: string, text?: string) => {
    const payload = new WebPayload<'shareImageWithText'>();
    payload.push('image', image);
    payload.push('text', text ?? '');
    this._dispatch('shareImageWithText', payload);
  };

  loadAd = (
    adType: AdType,
    unitId: string,
    provider: string = 'admob',
    supportsProvider: boolean = false
  ) => {
    const payload = new WebPayload<'loadAd'>();
    payload.push('adType', adType);
    payload.push('unitId', unitId);
    if (supportsProvider) {
      payload.push('provider', provider);
    }
    this._dispatch('loadAd', payload);
  };

  viewAd = (
    adType: AdType,
    unitId: string,
    provider: string = 'admob',
    supportsProvider: boolean = false
  ) => {
    const payload = new WebPayload<'viewAd'>();
    payload.push('adType', adType);
    payload.push('unitId', unitId);
    if (supportsProvider) {
      payload.push('provider', provider);
    }
    this._dispatch('viewAd', payload);
  };

  showAppReview = () => {
    this._dispatch('showAppReview', new WebPayload());
  };

  showToast = (message: string) => {
    if (!this.isNative) {
      useToastStore.getState().addToastList({ text: message });
      return;
    }
    const payload = new WebPayload<'showToast'>();
    payload.push('message', message);
    payload.push('length', 0);
    this._dispatch('showToast', payload);
  };

  openPhoneCertification = () => {
    const payload = new WebPayload<'openPhoneCertification'>();
    this._dispatch('openPhoneCertification', payload);
  };

  setIsBottomInputPage = (isBottomInput: boolean) => {
    const payload = new WebPayload<'setIsBottomInputPage'>();
    payload.push('isBottomInput', isBottomInput);
    this._dispatch('setIsBottomInputPage', payload);
  };

  openOssLicense = () => {
    this._dispatch('openOssLicense', new WebPayload());
  };

  setSwipeEnable = (isEnable: boolean) => {
    const payload = new WebPayload<'setSwipeEnable'>();
    payload.push('isEnable', isEnable);
    this._dispatch('setSwipeEnable', payload);
  };

  openSetting = () => {
    this._dispatch('openSetting', new WebPayload());
  };

  openAppSetting = () => {
    this._dispatch('openAppSetting', new WebPayload());
  };

  connectSNS = () => {
    this._dispatch('connectSNS', new WebPayload());
  };
  getPushSetting = () => {
    this._dispatch('getPushSetting', new WebPayload());
  };
  changeInitialTab = (type: string) => {
    const payload = new WebPayload<'changeInitialTab'>();
    payload.push('type', type);
    this._dispatch('changeInitialTab', payload);
  };
  getInitialTab = () => {
    const payload = new WebPayload<'getInitialTab'>();
    this._dispatch('getInitialTab', payload);
  };
  purchaseStoreItem = (bundleID: string) => {
    const payload = new WebPayload<'purchaseStoreItem'>();
    payload.push('bundleID', bundleID);

    this._dispatch('purchaseStoreItem', payload);
  };

  purchaseStoreItemByType = (
    bundleID: string,
    productType: ProductType = ProductType.inapp
  ) => {
    const payload = new WebPayload<'purchaseStoreItemByType'>();
    const product = {
      bundleID,
      productType,
    };
    payload.push('product', JSON.stringify(product));

    this._dispatch('purchaseStoreItemByType', payload);
  };

  reSubscribeStoreItem = (
    bundleID: string,
    productType: ProductType = ProductType.inapp
  ) => {
    const payload = new WebPayload<'purchaseStoreItemByType'>();
    const product = {
      bundleID,
      productType,
    };
    payload.push('product', JSON.stringify(product));

    this._dispatch('reSubscribeStoreItem', payload);
  };

  initPurchase = (paymentItems?: IPaymentItem[]) => {
    const payload = new WebPayload<'initPurchase'>();
    if (paymentItems?.length) {
      payload.push('paymentItems', jsonStringify(paymentItems));
    }
    this._dispatch('initPurchase', payload);
  };
  initializeBillingClient = () => {
    const payload = new WebPayload<'initializeBillingClient'>();

    this._dispatch('initializeBillingClient', payload);
  };
  openOfferwall = (type: string) => {
    const payload = new WebPayload<'openOfferwall'>();
    payload.push('type', type);
    this._dispatch('openOfferwall', payload);
  };
  initOfferwall = (userID?: string) => {
    const payload = new WebPayload<'initOfferwall'>();
    userID && payload.push('userID', userID);
    this._dispatch('initOfferwall', payload);
  };
  changeDevUrl = (url: string) => {
    const payload = new WebPayload<'changeDevUrl'>();
    payload.push('url', url);
    this._dispatch('changeDevUrl', payload);
  };
  changeNextDevUrl = (url: string) => {
    const payload = new WebPayload<'changeNextDevUrl'>();
    payload.push('url', url);
    this._dispatch('changeNextDevUrl', payload);
  };
}

/** App events */
type AppCommunicatedData =
  | {
      key: 'firebaseIdToken';
      value: {
        idToken: string;
      };
    }
  | {
      key: 'accessToken';
      value: {
        token: string;
      };
    };

type AppCommunicatedDataMap = UnionToMap<AppCommunicatedData>;

export type ChatieAppEvents = {
  setSessionId: { sessionId: string };
  transferABTestData: { data: object };
  transferRemoteConfigValue: { key: string; value: string };
  rerender: EmptyObject;
  data: AppCommunicatedData;
  actionSheetSelect: { name: string; value: number };
  loadedAd: { unitId: string };
  openedAd: { unitId: string };
  closedAd: { unitId: string };
  rewardedAd: { unitId: string };
  failedToLoadAd: { unitId: string; errorCode: number };
  induceAppReview: { value: number };
  isPushEnabled: { isEnabled: string };
  purchaseStoreItemResult: { resultType: string };
  initialTab: { initialTab: string };
};

export class AppInterface {
  private static instance: AppInterface;

  public static getInstance() {
    return this.instance || (this.instance = new this());
  }

  static listen<K extends keyof ChatieAppEvents>(
    name: K,
    listener: (e: CustomEvent<ChatieAppEvents[K]>) => void
  ) {
    window.addEventListener(name, listener);

    return () => {
      window.removeEventListener(name, listener);
    };
  }

  _dispatch = <K extends keyof ChatieAppEvents>(
    name: K,
    detail: ChatieAppEvents[K]
  ) => {
    const e = new CustomEvent(name, { detail });
    dispatchEvent(e);
  };

  setSessionId = (sessionId: string) => {
    this._dispatch('setSessionId', { sessionId });
  };

  transferABTestData = (data: object) => {
    this._dispatch('transferABTestData', { data });
  };

  transferRemoteConfigValue = (key: string, value: string) => {
    this._dispatch('transferRemoteConfigValue', { key, value });
  };

  rerender = () => {
    this._dispatch('rerender', {});
  };

  data = <K extends keyof AppCommunicatedDataMap>(
    key: K,
    value: AppCommunicatedDataMap[K]
  ) => {
    this._dispatch('data', { key, value } as AppCommunicatedData);
  };

  select = (name: string, value: number) => {
    this._dispatch('actionSheetSelect', { name, value });
  };

  loadedAd = (adType: AdType, unitId: string) => {
    this._dispatch('loadedAd', { unitId });
  };

  openedAd = (adType: AdType, unitId: string) => {
    this._dispatch('openedAd', { unitId });
  };

  closedAd = (adType: AdType, unitId: string) => {
    this._dispatch('closedAd', { unitId });
  };

  rewardedAd = (unitId: string) => {
    this._dispatch('rewardedAd', { unitId });
  };

  failedToLoadAd = (adType: string, unitId: string, errorCode: number) => {
    this._dispatch('failedToLoadAd', { unitId, errorCode });
  };

  induceAppReview = (value: number) => {
    this._dispatch('induceAppReview', { value });
  };
  isPushEnabled = (isEnabled: string) => {
    this._dispatch('isPushEnabled', { isEnabled });
  };
  purchaseStoreItemResult = (resultType: string) => {
    this._dispatch('purchaseStoreItemResult', { resultType });
  };

  initialTab = (initialTab: string) => {
    this._dispatch('initialTab', { initialTab });
  };
}

export const useWebView = () => {
  const web = useRef(WebInterface.getInstance());
  const app = useRef(AppInterface.getInstance());
  const router = useRouter();
  const { platform } = useGlobalContext();
  const isWeb = platform === Platform.web;

  const openPageOrMove = (url: string) => {
    if (isWeb) {
      return router.push(url);
    } else if (web?.current) {
      web.current.openPage(url);
    }
  };

  const safeGoBack = (prev?: string) => {
    const { navDepth } = usePrevUrlStore.getState();
    if (!isWeb && navDepth <= 0) {
      web.current.closePageOnBack();
    } else if (prev) {
      router.replace(prev);
    } else if (isWeb && navDepth <= 0) {
      router.replace('/');
    } else {
      router.back();
    }
  };

  const openPageSafe = (path: string) => {
    if (isWeb) {
      router.push(path);
    } else {
      web.current.openPage(path);
    }
  };

  const closePageSafe = () => {
    if (isWeb) {
      safeGoBack();
    } else {
      web.current.closePage();
    }
  };

  const openPopUpSafe = (url: string) => {
    if (isWeb) {
      window.open(url, '_blank');
    } else {
      web.current.openPopUpPage(url);
    }
  };

  const openExtUrlSafe = (url: string, hasToken?: boolean) => {
    if (isWeb) {
      window.open(url, '_blank');
    } else {
      web.current.openExtUrl(url, hasToken);
    }
  };

  const openUrlSafe = (url: string) => {
    if (isWeb) {
      const domain = process.env.NEXT_PUBLIC_DOMAIN || '';
      if (url.startsWith(domain) || url.startsWith('/')) {
        const path = url.startsWith(domain) ? url.slice(domain.length) : url;
        router.push(path);
      } else {
        window.open(url, '_blank');
      }
    } else {
      web.current.openUrl(url);
    }
  };

  const openLegacyPageSafe = (path: string) => {
    if (isWeb) {
      const url = new URL(path, process.env.NEXT_PUBLIC_DOMAIN_LEGACY);
      url.searchParams.set('from', 'next');
      window.open(url.href, '_blank');
    } else {
      web.current.openLegacyPage(path);
    }
  };

  const openPaymentSafe = (storyId: number, episodeId: number) => {
    if (isWeb) {
      web.current.showToast('앱에서 결제해주세요');
    } else {
      web.current.openPayment(storyId, episodeId);
    }
  };

  return {
    web: web.current,
    app: app.current,
    listen: AppInterface.listen,
    openPageOrMove,
    safeGoBack,
    openPageSafe,
    closePageSafe,
    openPopUpSafe,
    openExtUrlSafe,
    openUrlSafe,
    openLegacyPageSafe,
    openPaymentSafe,
  };
};

export const enum OfferwallType {
  tnk = 'tnk',
  common1 = 'common1',
  buzzvil = 'buzzvil',
  adison = 'adison',
  adpopcorn = 'adpopcorn',
  pincrux = 'pincrux',
  adchain = 'adchain',
}

export const useOfferwall = () => {
  const { firebase } = useAnalytics();
  const router = useRouter();

  const openOfferwall = (offerwallType: OfferwallType) => {
    const offerwallLink = `chatie-app://offerwall?type=${offerwallType}`;
    firebase.logEvent('induce_offerwall_click', { type: offerwallType });
    openDeepLink(offerwallLink, router);
  };

  return { openOfferwall };
};
